import { useEffect, useState } from "react";
import { Loader2, Landmark, PiggyBank, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatCard, Card, EstimateNote, money, downloadCSV, todayISO } from "./booksShared";

const MONTH_LABEL = (m) =>
  new Date(`${m}-15T00:00:00`).toLocaleDateString("en-US", { month: "short" });

const duePill = (days) => {
  if (days < 0) return { label: `Overdue by ${-days} day${-days === 1 ? "" : "s"}`, cls: "bg-terra text-parchment" };
  if (days === 0) return { label: "Due today!", cls: "bg-terra text-parchment" };
  if (days <= 7) return { label: `Due in ${days} day${days === 1 ? "" : "s"}`, cls: "bg-gold text-white" };
  return { label: `Due in ${days} days`, cls: "bg-parchment/15 text-parchment" };
};

export const BooksDashboard = () => {
  const [snap, setSnap] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const year = new Date().getFullYear();
    api.get("/admin/books/tax-snapshot").then((r) => setSnap(r.data)).catch(() => toast.error("Could not load tax snapshot"));
    api
      .get("/admin/books/summary", { params: { start: `${year}-01-01`, end: todayISO() } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load the dashboard"));
  }, []);

  const monthly = data?.monthly || [];
  const max = Math.max(1, ...monthly.map((m) => Math.max(m.sales, m.expenses, Math.abs(m.net))));

  const downloadReport = () => {
    if (!data || !snap) return;
    const year = snap.federal.year;
    downloadCSV(`picklemeok-report-${year}.csv`, [
      ["Pickle Me OK! — Year-to-Date Report", todayISO()],
      [],
      ["Total Sales", data.total_sales],
      ["Total Expenses", data.total_expenses],
      ["Net Profit", data.net_profit],
      ["Sales Tax Collected", data.sales_tax_collected],
      [`State Sales Tax Due (${snap.state.period_label})`, snap.state.amount],
      [`Estimated Federal Set-Aside (${year})`, snap.federal.est_federal_tax],
      [],
      ["Sales"],
      ["Date", "Customer", "Product", "Amount", "Tax Collected", "City", "Payment Method"],
      ...data.sales.map((s) => [s.date, s.customer, s.product, s.amount, s.tax_collected, s.city, s.payment_method]),
      [],
      ["Expenses"],
      ["Date", "Vendor", "Category", "Amount", "Deductible", "Notes"],
      ...data.expenses.map((e) => [e.date, e.vendor, e.category, e.amount, e.deductible ? "Yes" : "No", e.notes]),
    ]);
    toast.success("Report downloaded — opens in Excel");
  };

  if (!snap || !data) {
    return (
      <div className="flex justify-center py-16" data-testid="books-dashboard">
        <Loader2 className="w-6 h-6 animate-spin text-dill" />
      </div>
    );
  }

  const pill = duePill(snap.state.days_left);

  return (
    <div className="space-y-5" data-testid="books-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-dill text-parchment rounded-2xl p-6 flex flex-col" data-testid="state-tax-card">
          <div className="flex items-center gap-2 mb-1">
            <Landmark className="w-5 h-5 text-gold" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Pay the State</p>
          </div>
          <p className="text-sm text-parchment/70">Sales tax collected in {snap.state.period_label}</p>
          <p className="font-serif font-black text-5xl my-4" data-testid="state-tax-amount">{money(snap.state.amount)}</p>
          <div className="mt-auto flex flex-wrap items-center gap-3">
            <span className="font-mono text-xs text-parchment/70" data-testid="state-tax-due-date">Due {snap.state.due_label}</span>
            <span className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full ${pill.cls}`} data-testid="state-tax-countdown">
              {pill.label}
            </span>
          </div>
          <p className="text-[11px] text-parchment/50 mt-3">
            So far in {snap.collecting.period_label} you've collected {money(snap.collecting.amount_so_far)} toward the next payment.
          </p>
        </div>

        <div className="bg-white border-2 border-gold/40 rounded-2xl p-6 flex flex-col" data-testid="federal-tax-card">
          <div className="flex items-center gap-2 mb-1">
            <PiggyBank className="w-5 h-5 text-gold" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Federal — Year-End</p>
          </div>
          <p className="text-sm text-ink/50">Set aside for your yearly federal tax</p>
          <p className="font-serif font-black text-5xl my-4 text-gold" data-testid="federal-tax-amount">{money(snap.federal.est_federal_tax)}</p>
          <p className="text-sm text-ink/60 mt-auto">
            {snap.federal.rate}% of your {money(snap.federal.net_income)} profit so far in {snap.federal.year}.
          </p>
          <p className="text-[11px] text-ink/40 mt-2">Not due monthly — this covers you when you file your yearly return.</p>
        </div>
      </div>

      <Card title={`Sales Tax by City — ${snap.state.period_label}`} desc="Where this state payment's tax was collected." testid="tax-by-city-card">
        {snap.state.by_city.length === 0 ? (
          <p className="text-sm text-ink/50 italic">No sales in {snap.state.period_label} — nothing due this cycle.</p>
        ) : (
          <div className="space-y-2">
            {snap.state.by_city.map((c) => (
              <div key={c.city} className="flex items-center justify-between bg-parchdeep/50 rounded-xl px-4 py-3" data-testid={`tax-city-row-${c.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="text-sm font-semibold text-ink">{c.city}</span>
                <span className="font-mono text-xs text-ink/55">
                  {c.orders} order{c.orders === 1 ? "" : "s"} • <span className="font-bold text-dill">{money(c.tax)}</span>
                </span>
              </div>
            ))}
          </div>
        )}
        {snap.collecting.by_city.length > 0 && (
          <div className="border-t border-parchborder pt-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 mb-2">
              So far in {snap.collecting.period_label} (goes toward the next payment)
            </p>
            <div className="space-y-2">
              {snap.collecting.by_city.map((c) => (
                <div key={c.city} className="flex items-center justify-between px-4 py-1.5" data-testid={`tax-city-collecting-${c.city.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="text-sm text-ink/70">{c.city}</span>
                  <span className="font-mono text-xs text-ink/55">
                    {c.orders} order{c.orders === 1 ? "" : "s"} • {money(c.tax)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={`Sales in ${snap.month.label}`} value={money(snap.month.sales)} testid="stat-month-sales" />
        <StatCard label={`Expenses in ${snap.month.label}`} value={money(snap.month.expenses)} tone="text-terra" testid="stat-month-expenses" />
        <StatCard
          label={`Profit in ${snap.month.label}`}
          value={money(snap.month.profit)}
          tone={snap.month.profit >= 0 ? "text-dill" : "text-terra"}
          testid="stat-month-profit"
        />
        <StatCard label="Year-to-Date Sales" value={money(data.total_sales)} testid="stat-ytd-sales" />
      </div>

      <Card title="Month by Month" desc="Sales, expenses, and profit for the year so far." testid="books-monthly-chart">
        {monthly.length === 0 ? (
          <p className="text-sm text-ink/50 italic">No entries this year yet — sales from your shop appear here automatically.</p>
        ) : (
          <div className="flex items-end gap-4 sm:gap-6 h-44 pt-2 overflow-x-auto">
            {monthly.map((m) => (
              <div key={m.month} className="flex flex-col items-center gap-1.5 min-w-14">
                <div className="flex items-end gap-1 h-32">
                  <div className="w-3.5 bg-dill rounded-t" style={{ height: `${(m.sales / max) * 100}%` }} title={`Sales ${money(m.sales)}`} />
                  <div className="w-3.5 bg-terra rounded-t" style={{ height: `${(m.expenses / max) * 100}%` }} title={`Expenses ${money(m.expenses)}`} />
                  <div className="w-3.5 bg-gold rounded-t" style={{ height: `${(Math.abs(m.net) / max) * 100}%` }} title={`Net ${money(m.net)}`} />
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-ink/50">{MONTH_LABEL(m.month)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-5 font-mono text-[10px] uppercase tracking-widest text-ink/50 pt-2 border-t border-parchborder">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-dill inline-block" /> Sales</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-terra inline-block" /> Expenses</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-gold inline-block" /> Net Profit</span>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-4">
        <button
          onClick={downloadReport}
          className="flex items-center gap-2 bg-dill hover:bg-terra text-parchment font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
          data-testid="download-report-button"
        >
          <FileDown className="w-4 h-4" /> Download Full Report (CSV)
        </button>
        <p className="text-xs text-ink/45">Everything above plus every sale and expense — handy for your accountant.</p>
      </div>

      <EstimateNote />
    </div>
  );
};
